// Geometry for pencil strokes, shapes and arrows. Every function here has a
// twin with the same maths in api/moodboard_render.py, so a drawing exports
// exactly as it looks in the editor. Units are percent of the board width in
// both directions (the same as cqw).

export const BRUSHES = [
  { key: "pencil", label: "Pencil", stroke_width: 0.25 },
  { key: "marker", label: "Marker", stroke_width: 0.8 },
  { key: "highlighter", label: "Highlighter", stroke_width: 1.8, stroke: "#f2c94c" },
  { key: "brush", label: "Brush", stroke_width: 0.9 },
];
export const SHAPES = [
  { key: "rect", label: "Rectangle" },
  { key: "ellipse", label: "Ellipse" },
  { key: "triangle", label: "Triangle" },
  { key: "diamond", label: "Diamond" },
  { key: "hexagon", label: "Hexagon" },
  { key: "star", label: "Star" },
  { key: "bubble", label: "Speech bubble" },
];
export const ROUTES = [
  { key: "straight", label: "Straight" },
  { key: "curved", label: "Curved" },
  { key: "elbow", label: "Elbow" },
];
export const HEADS = [
  { key: "none", label: "None" },
  { key: "arrow", label: "Arrow" },
  { key: "open", label: "Open" },
  { key: "circle", label: "Circle" },
  { key: "square", label: "Square" },
  { key: "bar", label: "Bar" },
];
export const DASHES = [
  { key: "solid", label: "Solid" },
  { key: "dashed", label: "Dashed" },
  { key: "dotted", label: "Dotted" },
];

export const HIGHLIGHTER_ALPHA = 0.45;
export const DEFAULT_BEND = 0.35;
const SMOOTH_PASSES = 2;
const CURVE_STEPS = 40;
const ELLIPSE_STEPS = 72;
const ARC_STEPS = 8;

const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const scale = (a, k) => [a[0] * k, a[1] * k];
const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
const unit = (a) => {
  const length = Math.hypot(a[0], a[1]) || 1e-9;
  return [a[0] / length, a[1] / length];
};

// Chaikin corner cutting: turns a jittery pointer trail into a smooth curve
// while keeping both end points.
export function smooth(points) {
  let current = points;
  for (let pass = 0; pass < SMOOTH_PASSES && current.length >= 3; pass += 1) {
    const next = [current[0]];
    for (let index = 0; index < current.length - 1; index += 1) {
      const a = current[index];
      const b = current[index + 1];
      next.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]);
      next.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    next.push(current[current.length - 1]);
    current = next;
  }
  return current;
}

export function pathLength(points) {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) total += dist(points[index - 1], points[index]);
  return total;
}

// The calligraphic brush: an outline that swells from a fine start to full
// width and tapers off again at the end.
export function brushOutline(points, width) {
  if (points.length < 2) return [];
  const lengths = [0];
  for (let index = 1; index < points.length; index += 1) lengths.push(lengths[index - 1] + dist(points[index - 1], points[index]));
  const total = lengths[lengths.length - 1] || 1e-9;
  const left = [];
  const right = [];
  points.forEach((point, index) => {
    const prev = points[Math.max(index - 1, 0)];
    const next = points[Math.min(index + 1, points.length - 1)];
    const [dx, dy] = unit(sub(next, prev));
    const t = lengths[index] / total;
    const taper = Math.max(0, Math.min(1, t / 0.18, (1 - t) / 0.28));
    const half = (width * (0.12 + 0.88 * taper)) / 2;
    left.push([point[0] - dy * half, point[1] + dx * half]);
    right.push([point[0] + dy * half, point[1] - dx * half]);
  });
  return [...left, ...right.reverse()];
}

function arc(cx, cy, r, from, to) {
  const points = [];
  for (let step = 0; step <= ARC_STEPS; step += 1) {
    const angle = from + ((to - from) * step) / ARC_STEPS;
    points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
  }
  return points;
}

// A closed outline (first point not repeated) filling the box x, y, w, h.
export function shapePoints(shape, x, y, w, h, radius = 0) {
  const right = x + w;
  const bottom = y + h;
  const half = Math.PI / 2;
  switch (shape) {
    case "ellipse": {
      const points = [];
      for (let step = 0; step < ELLIPSE_STEPS; step += 1) {
        const angle = -half + (2 * Math.PI * step) / ELLIPSE_STEPS;
        points.push([x + w / 2 + (w / 2) * Math.cos(angle), y + h / 2 + (h / 2) * Math.sin(angle)]);
      }
      return points;
    }
    case "triangle":
      return [[x + w / 2, y], [right, bottom], [x, bottom]];
    case "diamond":
      return [[x + w / 2, y], [right, y + h / 2], [x + w / 2, bottom], [x, y + h / 2]];
    case "hexagon":
      return [[x + w * 0.25, y], [x + w * 0.75, y], [right, y + h / 2], [x + w * 0.75, bottom], [x + w * 0.25, bottom], [x, y + h / 2]];
    case "star": {
      const raw = [];
      for (let step = 0; step < 10; step += 1) {
        const angle = -half + (step * Math.PI) / 5;
        const r = step % 2 === 0 ? 1 : 0.42;
        raw.push([r * Math.cos(angle), r * Math.sin(angle)]);
      }
      const xs = raw.map((p) => p[0]);
      const ys = raw.map((p) => p[1]);
      const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      return raw.map(([px, py]) => [x + ((px - minX) / (maxX - minX)) * w, y + ((py - minY) / (maxY - minY)) * h]);
    }
    case "bubble": {
      const body = h * 0.78;
      const r = Math.min(w, body) * 0.18;
      return [
        ...arc(x + r, y + r, r, Math.PI, Math.PI * 1.5),
        ...arc(right - r, y + r, r, -half, 0),
        ...arc(right - r, y + body - r, r, 0, half),
        [x + w * 0.38, y + body],
        [x + w * 0.14, bottom],
        [x + w * 0.22, y + body],
        ...arc(x + r, y + body - r, r, half, Math.PI),
      ];
    }
    default: {
      const r = Math.max(0, Math.min(radius, w / 2, h / 2));
      if (r <= 1e-6) return [[x, y], [right, y], [right, bottom], [x, bottom]];
      return [
        ...arc(right - r, y + r, r, -half, 0),
        ...arc(right - r, bottom - r, r, 0, half),
        ...arc(x + r, bottom - r, r, half, Math.PI),
        ...arc(x + r, y + r, r, Math.PI, Math.PI * 1.5),
      ];
    }
  }
}

// Removes `fromStart` and `fromEnd` of length from a polyline, so a line stops
// where its arrowhead begins.
export function trimPath(points, fromStart, fromEnd) {
  const total = pathLength(points);
  if (total <= 1e-9) return points;
  const budget = total * 0.98;
  if (fromStart + fromEnd > budget) {
    const factor = budget / (fromStart + fromEnd);
    fromStart *= factor;
    fromEnd *= factor;
  }
  const cutStart = (path, amount) => {
    let remaining = amount;
    for (let index = 1; index < path.length; index += 1) {
      const length = dist(path[index - 1], path[index]);
      if (length >= remaining) {
        const k = length ? remaining / length : 0;
        return [add(path[index - 1], scale(sub(path[index], path[index - 1]), k)), ...path.slice(index)];
      }
      remaining -= length;
    }
    return path.slice(-1);
  };
  let result = fromStart > 0 ? cutStart(points, fromStart) : points;
  if (fromEnd > 0) result = cutStart([...result].reverse(), fromEnd).reverse();
  return result;
}

// Older arrows stored a single `head` of "end", "both" or "none".
export function arrowHeads(item) {
  return {
    start: item.head_start ?? (item.head === "both" ? "arrow" : "none"),
    end: item.head_end ?? (item.head === "none" ? "none" : "arrow"),
  };
}

function arrowPath(p0, p1, route, bend) {
  if (route === "curved") {
    const length = dist(p0, p1);
    if (length < 1e-6) return [p0, p1];
    const mid = scale(add(p0, p1), 0.5);
    const normal = [-(p1[1] - p0[1]) / length, (p1[0] - p0[0]) / length];
    const control = add(mid, scale(normal, bend * length));
    const points = [];
    for (let step = 0; step <= CURVE_STEPS; step += 1) {
      const t = step / CURVE_STEPS;
      const a = (1 - t) * (1 - t);
      const b = 2 * (1 - t) * t;
      const c = t * t;
      points.push([a * p0[0] + b * control[0] + c * p1[0], a * p0[1] + b * control[1] + c * p1[1]]);
    }
    return points;
  }
  if (route === "elbow") {
    const [dx, dy] = sub(p1, p0);
    const corners =
      Math.abs(dx) >= Math.abs(dy)
        ? [[(p0[0] + p1[0]) / 2, p0[1]], [(p0[0] + p1[0]) / 2, p1[1]]]
        : [[p0[0], (p0[1] + p1[1]) / 2], [p1[0], (p0[1] + p1[1]) / 2]];
    return [p0, ...corners, p1].filter((point, index, all) => index === 0 || dist(point, all[index - 1]) > 1e-9);
  }
  return [p0, p1];
}

// The control point of a curved arrow sits at its middle; this is where the
// bend handle is drawn.
export function curveMidpoint(p0, p1, bend) {
  const length = dist(p0, p1);
  const mid = scale(add(p0, p1), 0.5);
  if (length < 1e-6) return mid;
  const normal = [-(p1[1] - p0[1]) / length, (p1[0] - p0[0]) / length];
  return add(mid, scale(normal, (bend * length) / 2));
}

export function bendFromPoint(p0, p1, point) {
  const length = dist(p0, p1);
  if (length < 1e-6) return 0;
  const mid = scale(add(p0, p1), 0.5);
  const normal = [-(p1[1] - p0[1]) / length, (p1[0] - p0[0]) / length];
  const offset = (point[0] - mid[0]) * normal[0] + (point[1] - mid[1]) * normal[1];
  return Math.max(-1, Math.min(1, (2 * offset) / length));
}

function headShape(type, tip, direction, headLen, strokeWidth) {
  const [dx, dy] = direction;
  const normal = [-dy, dx];
  const half = headLen * 0.5;
  const back = sub(tip, scale(direction, headLen));
  switch (type) {
    case "arrow":
      return { trim: headLen * 0.8, parts: [{ kind: "fill", points: [tip, add(back, scale(normal, half)), sub(back, scale(normal, half))] }] };
    case "open":
      return { trim: strokeWidth * 0.5, parts: [{ kind: "stroke", points: [add(back, scale(normal, half)), tip, sub(back, scale(normal, half))] }] };
    case "circle": {
      const r = headLen * 0.32;
      return { trim: r, parts: [{ kind: "circle", center: tip, r }] };
    }
    case "square": {
      const side = headLen * 0.55;
      const along = scale(direction, side / 2);
      const across = scale(normal, side / 2);
      return {
        trim: side / 2,
        parts: [{ kind: "fill", points: [add(add(tip, along), across), sub(add(tip, along), across), sub(sub(tip, along), across), add(sub(tip, along), across)] }],
      };
    }
    case "bar":
      return { trim: 0, parts: [{ kind: "stroke", points: [add(tip, scale(normal, half * 1.1)), sub(tip, scale(normal, half * 1.1))] }] };
    default:
      return { trim: 0, parts: [] };
  }
}

// The line and its arrowheads for an arrow from p0 to p1.
export function arrowGeometry(p0, p1, strokeWidth, { route = "straight", bend = DEFAULT_BEND, start = "none", end = "arrow" } = {}) {
  const path = arrowPath(p0, p1, route, bend);
  const total = pathLength(path);
  const both = start !== "none" && end !== "none";
  const headLen = Math.min(Math.max(strokeWidth * 4, 1.6), total * (both ? 0.45 : 0.9));
  const parts = [];
  let trimStart = 0;
  let trimEnd = 0;
  if (path.length >= 2 && total > 1e-6) {
    const endHead = headShape(end, path[path.length - 1], unit(sub(path[path.length - 1], path[path.length - 2])), headLen, strokeWidth);
    const startHead = headShape(start, path[0], unit(sub(path[0], path[1])), headLen, strokeWidth);
    parts.push(...endHead.parts, ...startHead.parts);
    trimEnd = endHead.trim;
    trimStart = startHead.trim;
  }
  return { path: trimPath(path, trimStart, trimEnd), parts };
}

// Everything a drawing is made of, in units relative to its own box. Twin of
// _vector_parts() in api/moodboard_render.py.
export function vectorParts(item, ratio) {
  const width = item.w;
  const height = item.h / ratio;
  const sw = item.stroke_width || 0;
  let dash = item.dash || "solid";
  const parts = [];

  if (item.type === "shape") {
    const inset = sw > 0 ? sw / 2 : 0;
    const outer = ((item.radius || 0) / 100) * Math.min(width, height);
    const points = shapePoints(
      item.shape || "rect",
      inset,
      inset,
      Math.max(width - 2 * inset, 0.01),
      Math.max(height - 2 * inset, 0.01),
      Math.max(0, outer - inset)
    );
    if (item.fill) parts.push({ kind: "fill", points, color: item.fill });
    if (sw > 0) parts.push({ kind: "line", points, closed: true, roundCaps: true, dash });
  } else if (item.type === "draw") {
    const points = smooth((item.points || []).map(([x, y]) => [x * width, y * height]));
    const brush = item.brush || "pencil";
    if (brush === "brush") {
      parts.push({ kind: "fill", points: brushOutline(points, sw) });
    } else {
      if (brush === "highlighter" && dash === "dotted") dash = "dashed";
      parts.push({ kind: "line", points, closed: false, roundCaps: brush !== "highlighter", dash });
    }
  } else if (item.points?.length === 2) {
    const [[ax, ay], [bx, by]] = item.points;
    const heads = arrowHeads(item);
    const { path, parts: headParts } = arrowGeometry([ax * width, ay * height], [bx * width, by * height], sw, {
      route: item.route || "straight",
      bend: item.bend ?? DEFAULT_BEND,
      start: heads.start,
      end: heads.end,
    });
    parts.push({ kind: "line", points: path, closed: false, roundCaps: true, dash });
    headParts.forEach((part) =>
      parts.push(part.kind === "stroke" ? { kind: "line", points: part.points, closed: false, roundCaps: true, dash: "solid" } : part)
    );
  }
  return parts;
}

// SVG dash pattern; the export walks the same pattern by hand.
export function dashArray(dash, strokeWidth) {
  if (dash === "dashed") return `${strokeWidth * 4} ${strokeWidth * 3}`;
  if (dash === "dotted") return `0.001 ${strokeWidth * 2.2}`;
  return undefined;
}
